import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "../store/authStore";
import { ArrowLeft, PlugsConnected, Globe, Lightning, Copy, ArrowSquareOut } from "@phosphor-icons/react";
import { toast } from "sonner";

const S = {
  bg:       '#0a0a0f',
  white:    '#13131f',
  border:   'rgba(124,58,237,0.2)',
  borderMd: 'rgba(124,58,237,0.4)',
  text:     '#e8e8f0',
  muted:    '#9090b0',
  hint:     '#5a5a7a',
  indigo:   '#7c3aed',
};

export default function DownloadsPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [siteId, setSiteId] = useState(null);
  const [apiKey, setApiKey] = useState(null);
  const [copied, setCopied] = useState('');

  useEffect(() => {
    if (user) {
      setSiteId(`site_${user.user_id}`);
      const stored = localStorage.getItem(`apikey_${user.user_id}`);
      if (stored) { setApiKey(stored); }
      else {
        const key = `wp_${Math.random().toString(36).substring(2,15)}${Math.random().toString(36).substring(2,10)}`;
        localStorage.setItem(`apikey_${user.user_id}`, key);
        setApiKey(key);
      }
    }
  }, [user]);

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    toast.success(`${label} copied!`);
    setTimeout(() => setCopied(''), 2000);
  };

  const scriptCode = `<script\n  src="https://seodash360.com/scripts/seo-dash-360.js"\n  data-site-id="${siteId || 'YOUR_SITE_ID'}"\n  data-api-key="${apiKey || 'YOUR_API_KEY'}"\n  async defer>\n</script>`;

  const card = { background:S.white, border:`1px solid ${S.border}`, borderRadius:12, padding:'24px 28px', marginBottom:16 };

  return (
    <div style={{ minHeight:'100vh', background:S.bg, fontFamily:"'IBM Plex Sans', system-ui, sans-serif", color:S.text }}>
      <header style={{ background:S.white, borderBottom:`1px solid ${S.border}`, position:'sticky', top:0, zIndex:50 }}>
        <div style={{ maxWidth:860, margin:'0 auto', padding:'0 24px', height:52, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div style={{ display:'flex', alignItems:'center', gap:14 }}>
            <button onClick={() => navigate('/dashboard')}
              style={{ display:'flex', alignItems:'center', gap:6, padding:'5px 10px', background:'transparent', border:`1px solid ${S.border}`, borderRadius:6, fontSize:13, color:S.muted, cursor:'pointer' }}
              onMouseEnter={e=>e.currentTarget.style.borderColor=S.borderMd}
              onMouseLeave={e=>e.currentTarget.style.borderColor=S.border}>
              <ArrowLeft style={{ width:13, height:13 }} /> Dashboard
            </button>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <div style={{ width:28, height:28, background:'rgba(236,72,153,0.12)', borderRadius:7, display:'flex', alignItems:'center', justifyContent:'center' }}>
                <Lightning weight="bold" style={{ width:14, height:14, color:'#ec4899' }} />
              </div>
              <h1 style={{ fontFamily:"'Satoshi', sans-serif", fontSize:16, fontWeight:800, color:S.text, margin:0, letterSpacing:'-0.02em' }}>Downloads</h1>
            </div>
          </div>
          {user && (
            <span style={{ fontSize:10, fontWeight:700, padding:'3px 7px', borderRadius:4, background:S.bg, color:S.hint, letterSpacing:'0.06em', textTransform:'uppercase' }}>
              {user.plan || 'free'}
            </span>
          )}
        </div>
      </header>

      <main style={{ maxWidth:860, margin:'0 auto', padding:'36px 24px' }}>
        <div style={{ marginBottom:28 }}>
          <h1 style={{ fontFamily:"'Satoshi', sans-serif", fontSize:28, fontWeight:900, color:S.text, margin:0, letterSpacing:'-0.04em' }}>Connect your site</h1>
          <p style={{ fontSize:13, color:S.hint, margin:'5px 0 0' }}>Install the WordPress plugin or add the universal script to sync your content automatically.</p>
        </div>

        {/* API credentials */}
        <div style={{ ...card, background:'rgba(124,58,237,0.06)', border:`1px solid ${S.border}` }}>
          <p style={{ fontSize:11, fontWeight:700, color:S.hint, margin:'0 0 14px', letterSpacing:'0.07em', textTransform:'uppercase' }}>Your API credentials</p>
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {[
              { label:'Site ID', value:siteId || 'Generating...' },
              { label:'API Key', value:apiKey || 'Generating...' },
            ].map(item => (
              <div key={item.label} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', background:S.white, border:`1px solid ${S.border}`, borderRadius:8, padding:'10px 14px' }}>
                <span style={{ fontSize:12, fontWeight:600, color:S.muted, minWidth:70 }}>{item.label}</span>
                <code style={{ fontSize:12, color:S.text, flex:1, margin:'0 12px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontFamily:"'JetBrains Mono', monospace" }}>
                  {item.value}
                </code>
                <button onClick={() => copy(item.value, item.label)}
                  style={{ display:'flex', alignItems:'center', gap:5, padding:'4px 10px', background:copied===item.label?'rgba(16,185,129,0.15)':S.bg, border:`1px solid ${S.border}`, borderRadius:5, fontSize:11, fontWeight:600, color:copied===item.label?'#4ade80':S.muted, cursor:'pointer', whiteSpace:'nowrap' }}>
                  <Copy style={{ width:11, height:11 }} />
                  {copied===item.label ? 'Copied!' : 'Copy'}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* WordPress plugin */}
        <div style={card}>
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12 }}>
            <div style={{ width:32, height:32, background:'rgba(124,58,237,0.12)', borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center' }}>
              <PlugsConnected weight="bold" style={{ width:16, height:16, color:S.indigo }} />
            </div>
            <div>
              <h2 style={{ fontFamily:"'Satoshi', sans-serif", fontSize:16, fontWeight:800, color:S.text, margin:0, letterSpacing:'-0.02em' }}>WordPress plugin</h2>
              <p style={{ fontSize:12, color:S.hint, margin:0 }}>For WordPress sites</p>
            </div>
          </div>
          <p style={{ fontSize:13, color:S.muted, margin:'0 0 18px', lineHeight:1.6 }}>
            Download and install the plugin on your WordPress site. Enter your API key in the plugin settings and click <strong style={{ color:S.text }}>Synchronize</strong> — your posts will be imported automatically.
          </p>
          <div style={{ background:S.bg, border:`1px solid ${S.border}`, borderRadius:8, padding:'14px 16px', marginBottom:18 }}>
            <p style={{ fontSize:11, fontWeight:700, color:S.hint, margin:'0 0 10px', letterSpacing:'0.06em', textTransform:'uppercase' }}>Installation steps</p>
            {['Download the plugin (.zip file)','Go to WordPress → Plugins → Add New → Upload','Upload the .zip file and activate','Go to Settings → SEO Dash 360','Paste your API Key and Site ID','Click Synchronize — done!'].map((step, i) => (
              <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:10, marginBottom:6 }}>
                <span style={{ fontSize:10, fontWeight:700, color:'#fff', background:S.indigo, borderRadius:'50%', width:16, height:16, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, marginTop:1 }}>{i+1}</span>
                <span style={{ fontSize:12, color:S.muted, lineHeight:1.5 }}>{step}</span>
              </div>
            ))}
          </div>
          <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
            <a href="/seo-dash-360-wordpress-plugin.zip"
              style={{ display:'inline-flex', alignItems:'center', gap:7, padding:'9px 18px', background:'linear-gradient(135deg,#7c3aed,#3b82f6)', color:'#fff', borderRadius:8, fontSize:13, fontWeight:600, textDecoration:'none' }}>
              <Lightning weight="bold" style={{ width:13, height:13 }} /> Download WordPress plugin
            </a>
            <button onClick={() => window.open('https://seodash360.com/guide', '_blank')}
              style={{ display:'inline-flex', alignItems:'center', gap:7, padding:'9px 18px', background:'transparent', color:S.muted, border:`1px solid ${S.border}`, borderRadius:8, fontSize:13, fontWeight:500, cursor:'pointer' }}
              onMouseEnter={e=>e.currentTarget.style.borderColor=S.borderMd}
              onMouseLeave={e=>e.currentTarget.style.borderColor=S.border}>
              <ArrowSquareOut style={{ width:13, height:13 }} /> View docs
            </button>
          </div>
        </div>

        {/* Universal script */}
        <div style={card}>
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12 }}>
            <div style={{ width:32, height:32, background:'rgba(22,163,74,0.12)', borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center' }}>
              <Globe weight="bold" style={{ width:16, height:16, color:'#4ade80' }} />
            </div>
            <div>
              <h2 style={{ fontFamily:"'Satoshi', sans-serif", fontSize:16, fontWeight:800, color:S.text, margin:0, letterSpacing:'-0.02em' }}>Universal script</h2>
              <p style={{ fontSize:12, color:S.hint, margin:0 }}>For Shopify, Wix, Squarespace, or any site</p>
            </div>
          </div>
          <p style={{ fontSize:13, color:S.muted, margin:'0 0 14px', lineHeight:1.6 }}>
            Paste this script in the <code style={{ background:'rgba(124,58,237,0.1)', padding:'1px 5px', borderRadius:4, fontSize:12, fontFamily:'monospace', color:'#a78bfa' }}>&lt;head&gt;</code> section of your site. Your content will sync automatically.
          </p>
          <div style={{ background:'#0a0a0f', border:`1px solid ${S.border}`, borderRadius:8, padding:'16px 18px', marginBottom:14, overflowX:'auto' }}>
            <pre style={{ margin:0, fontSize:12, lineHeight:1.7, fontFamily:"'JetBrains Mono', monospace", color:'#a3e635', whiteSpace:'pre-wrap', wordBreak:'break-all' }}>
{`<script
  src="https://seodash360.com/scripts/seo-dash-360.js"
  data-site-id="${siteId || 'YOUR_SITE_ID'}"
  data-api-key="${apiKey || 'YOUR_API_KEY'}"
  async defer>
</script>`}
            </pre>
          </div>
          <div style={{ display:'flex', gap:10 }}>
            <button onClick={() => copy(scriptCode, 'Script')}
              style={{ display:'inline-flex', alignItems:'center', gap:7, padding:'8px 16px', background:copied==='Script'?'rgba(16,185,129,0.15)':'linear-gradient(135deg,#7c3aed,#3b82f6)', color:copied==='Script'?'#4ade80':'#fff', border:'none', borderRadius:7, fontSize:13, fontWeight:600, cursor:'pointer' }}>
              <Copy style={{ width:13, height:13 }} />
              {copied==='Script' ? 'Copied!' : 'Copy script'}
            </button>
            <a href="/scripts/seo-dash-360.js" download
              style={{ display:'inline-flex', alignItems:'center', gap:7, padding:'8px 16px', background:'transparent', color:S.muted, border:`1px solid ${S.border}`, borderRadius:7, fontSize:13, fontWeight:500, textDecoration:'none' }}>
              <Lightning style={{ width:13, height:13 }} /> Download script
            </a>
          </div>
        </div>

        {/* How it works */}
        <div style={{ background:'rgba(124,58,237,0.08)', border:'1px solid rgba(124,58,237,0.3)', borderRadius:12, padding:'20px 24px' }}>
          <p style={{ fontSize:13, fontWeight:700, color:'#a78bfa', margin:'0 0 12px' }}>How it works</p>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6px 24px' }}>
            {['Plugin/script automatically collects your posts','Data is sent to SEO Dash 360 securely','Audit analyzes titles, slugs and content','Corrections are shown in your dashboard','Apply changes manually or automatically','Re-syncs automatically on every new post'].map((item, i) => (
              <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:8, fontSize:12, color:'#a78bfa', lineHeight:1.5, padding:'3px 0' }}>
                <span style={{ color:S.indigo, marginTop:1, flexShrink:0 }}>→</span>
                {item}
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
